import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useTranslate } from 'src/locales';
import { useUploadFileMutation } from 'src/services/files/file.service';
import { PresentationKind, WordTemplateCheck, PresentationErrorCodes } from 'src/services/presentations/presentations.types';
import {
  useCheckWordTemplateMutation,
  useCreateWordTemplateMutation,
  useApplyWordPlaceholdersMutation,
  useSuggestWordPlaceholdersMutation,
  useReplaceWordTemplateFileMutation,
  useGetWordTemplatePlaceholdersQuery,
} from 'src/services/presentations/presentations.service';

import Iconify from 'src/components/iconify';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';

import PlaceholderSuggestionsPanel, { PlaceholderSuggestions } from './placeholder-suggestions';

const KINDS = [PresentationKind.FORMATTED_RESUME, PresentationKind.PROFILE, PresentationKind.EXECUTIVE, PresentationKind.COVER_PLUS_RESUME];
const DOCX = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  /** Whether PDFs can be made from Word here; Word exports work either way. */
  wordConversion: boolean;
  /** Upload a new version of this Word template instead of adding a template. */
  replacing?: { key: string; name: string } | null;
}

/**
 * Upload an agency Word template (JT1-1887): choose the .docx, see what the check found, then save.
 * A template with problems can't be saved, so nothing reaches recruiters that would print blanks
 * or be refused.
 */
export default function WordTemplateDialog({ open, onClose, wordConversion, replacing = null }: Props) {
  const { t } = useTranslate();
  const [upload] = useUploadFileMutation();
  const [checkTemplate] = useCheckWordTemplateMutation();
  const [createTemplate, { isLoading: creating }] = useCreateWordTemplateMutation();
  const [replaceFile, { isLoading: replacingFile }] = useReplaceWordTemplateFileMutation();
  const saving = creating || replacingFile;
  const [busy, setBusy] = useState(false);
  const [fileId, setFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [check, setCheck] = useState<WordTemplateCheck | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<PresentationKind>(PresentationKind.FORMATTED_RESUME);
  const [isDefault, setIsDefault] = useState(false);
  const [showReference, setShowReference] = useState(false);
  const { canAccess } = useFeatures();
  const aiAvailable = canAccess(FeatureType.AI_SOURCING_ENABLED);
  const [suggestPlaceholders, { isLoading: suggesting }] = useSuggestWordPlaceholdersMutation();
  const [applyPlaceholders, { isLoading: applying }] = useApplyWordPlaceholdersMutation();
  const [suggestions, setSuggestions] = useState<PlaceholderSuggestions | null>(null);
  const untagged = !!check?.ok && check.tags.length === 0;

  const suggest = async () => {
    if (!fileId) return;
    try {
      setSuggestions(await suggestPlaceholders({ file_id: fileId }).unwrap());
    } catch {
      enqueueSnackbar(t('presentations.ai.failed'), { variant: 'error' });
    }
  };

  // The tagged copy comes back as a new upload with its own check, and replaces the original here.
  const apply = async (chosen: PlaceholderSuggestions['placeholders']) => {
    if (!fileId) return;
    try {
      const result = await applyPlaceholders({ file_id: fileId, placeholders: chosen }).unwrap();
      setFileId(result.file_id);
      setCheck(result.check);
      setSuggestions(null);
      enqueueSnackbar(t('presentations.word.ai_applied', { count: result.applied }), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('presentations.word.ai_apply_failed'), { variant: 'error' });
    }
  };

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setCheck(null);
    setSuggestions(null);
    try {
      const uploaded = await upload({ file, type: 'document' }).unwrap();
      setFileId(uploaded.id);
      setFileName(file.name);
      if (!name && !replacing) setName(file.name.replace(/\.docx$/i, '').slice(0, 120));
      setCheck(await checkTemplate({ file_id: uploaded.id }).unwrap());
    } catch (error) {
      const found = (error as { data?: { context?: { check?: WordTemplateCheck } } })?.data?.context?.check;
      if (found) setCheck(found);
      else enqueueSnackbar(t('presentations.word.upload_failed'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!fileId) return;
    try {
      if (replacing) {
        await replaceFile({ key: replacing.key, file_id: fileId }).unwrap();
        enqueueSnackbar(t('presentations.word.new_version_saved', { name: replacing.name }), { variant: 'success' });
      } else {
        await createTemplate({ file_id: fileId, name: name.trim(), kind, is_default: isDefault }).unwrap();
        enqueueSnackbar(t('presentations.word.saved'), { variant: 'success' });
      }
      onClose();
    } catch (error) {
      const data = (error as { data?: { error_code?: string; context?: { check?: WordTemplateCheck } } })?.data;
      if (data?.error_code === PresentationErrorCodes.InvalidWordTemplate && data.context?.check) setCheck(data.context.check);
      else enqueueSnackbar(t('presentations.word.save_failed'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{replacing ? t('presentations.word.new_version_title', { name: replacing.name }) : t('presentations.word.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('presentations.word.intro')}</Typography>
          {replacing && <Alert severity="info">{t('presentations.word.new_version_note')}</Alert>}
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.neutral', fontFamily: 'monospace', fontSize: 13 }}>
            {'{candidate.full_name}'}<br />
            {'{FOR role IN employment}{$role.position_title}, {$role.employer}{END-FOR role}'}<br />
            {'{IF has.languages} ... {END-IF}'}
          </Box>
          <Button size="small" color="inherit" onClick={() => setShowReference((s) => !s)} startIcon={<Iconify icon={showReference ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'} />} sx={{ alignSelf: 'flex-start' }}>
            {t('presentations.word.reference')}
          </Button>
          <Collapse in={showReference} unmountOnExit>
            <PlaceholderReference />
          </Collapse>

          {!wordConversion && <Alert severity="info">{t('presentations.word.no_converter')}</Alert>}

          <Stack direction="row" spacing={2} alignItems="center">
            <LoadingButton component="label" variant="outlined" color="inherit" loading={busy} startIcon={<Iconify icon="solar:upload-bold" />}>
              {fileId ? t('presentations.word.replace_file') : t('presentations.word.choose_file')}
              <input hidden type="file" accept={DOCX} onChange={(event) => choose(event.target.files?.[0])} />
            </LoadingButton>
            {fileName && <Typography variant="body2" noWrap>{fileName}</Typography>}
          </Stack>

          {check && !untagged && <CheckResult check={check} />}
          {untagged && (
            <Alert severity="info">
              <Typography variant="body2">{t('presentations.word.no_placeholders')}</Typography>
              {aiAvailable && !suggestions && (
                <>
                  <Typography variant="caption" component="p" sx={{ mt: 0.5, color: 'text.secondary' }}>{t('presentations.word.ai_privacy')}</Typography>
                  <LoadingButton size="small" variant="soft" color="secondary" loading={suggesting} onClick={suggest} startIcon={<Iconify icon="solar:magic-stick-3-bold" />} sx={{ mt: 1 }}>
                    {t('presentations.word.ai_suggest')}
                  </LoadingButton>
                </>
              )}
            </Alert>
          )}
          {suggestions && <PlaceholderSuggestionsPanel suggestions={suggestions} applying={applying} onApply={apply} />}

          {check?.ok && !untagged && !replacing && (
            <Stack spacing={2}>
              <TextField size="small" label={t('presentations.word.name')} value={name} onChange={(event) => setName(event.target.value)} inputProps={{ maxLength: 120 }} />
              <TextField select size="small" label={t('presentations.create.type')} value={kind} onChange={(event) => setKind(event.target.value as PresentationKind)} helperText={t('presentations.word.type_helper')}>
                {KINDS.map((value) => <MenuItem key={value} value={value}>{t(`presentations.kinds.${value}.label`)}</MenuItem>)}
              </TextField>
              <FormControlLabel control={<Checkbox checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} />} label={t('presentations.template_dialog.make_default')} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={saving} disabled={!check?.ok || untagged || (!replacing && !name.trim())} onClick={save}>
          {t('presentations.word.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function CheckResult({ check }: { check: WordTemplateCheck }) {
  const { t } = useTranslate();
  return (
    <Stack spacing={1}>
      {check.ok && <Alert severity="success">{t('presentations.word.check_ok', { count: check.tags.length })}</Alert>}
      {!!check.invalid.length && (
        <Alert severity="error">
          <Typography variant="body2" sx={{ mb: 0.5 }}>{t('presentations.word.check_invalid')}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {check.invalid.map((problem) => <li key={problem}><Typography variant="body2">{problem}</Typography></li>)}
          </Box>
        </Alert>
      )}
      {!!check.unknown.length && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ mb: 0.5 }}>{t('presentations.word.check_unknown')}</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {check.unknown.map((tag) => <Chip key={tag} size="small" label={`{${tag}}`} />)}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

/** Every placeholder, click to copy. */
function PlaceholderReference() {
  const { t } = useTranslate();
  const { currentData: placeholders = [] } = useGetWordTemplatePlaceholdersQuery();

  const copy = (tag: string) => {
    navigator.clipboard?.writeText(tag).then(() => enqueueSnackbar(t('presentations.word.copied', { tag }), { variant: 'success' })).catch(() => undefined);
  };

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ maxHeight: 220, overflow: 'auto' }}>
      {placeholders.map((p) => {
        // Inside a loop a field is read through its variable: {$item.employer}.
        const field = p.tag.split('[].')[1];
        let tag = `{${p.tag}}`;
        if (p.repeats) tag = `{FOR item IN ${p.tag}}{$item}{END-FOR item}`;
        else if (field) tag = `{$item.${field}}`;
        return (
          <Tooltip key={p.tag} title={p.repeats ? t('presentations.word.repeats') : ''}>
            <Chip size="small" variant="outlined" label={p.tag} onClick={() => copy(tag)} />
          </Tooltip>
        );
      })}
    </Stack>
  );
}
